import React from 'react';
import { Plus, Trash2, ChevronDown, Check, Utensils, Package, Minus, AlertCircle } from 'lucide-react';
import { MealTypes } from '../../../utils/Constants';

// Custom Categories Dropdown Component (moved here to be reusable)
function CategoriesDropdown({ categories, selectedIds, onChange, placeholder }) {
  const [isOpen, setIsOpen] = React.useState(false);
  const dropdownRef = React.useRef(null);

  // Close dropdown when clicking outside
  React.useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    
    // Close on escape key
    function handleEscapeKey(event) {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscapeKey);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleEscapeKey);
      };
    }
  }, [isOpen]);

  const handleToggleCategory = (categoryId) => {
    const newIds = selectedIds.includes(categoryId)
      ? selectedIds.filter(id => id !== categoryId)
      : [...selectedIds, categoryId];
    onChange(newIds);
  };

  const selectedCategories = categories.filter(cat => selectedIds.includes(cat.id));

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 flex items-center justify-between cursor-pointer hover:bg-gray-50 transition-colors text-left"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className="text-sm flex-1 truncate">
          {selectedIds.length > 0 ? (
            selectedIds.length === 1 ? (
              selectedCategories[0]?.nameEn || selectedCategories[0]?.name || "Unknown Category"
            ) : (
              `${selectedIds.length} categories selected`
            )
          ) : (
            <span className="text-gray-500">{placeholder}</span>
          )}
        </span>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="fixed inset-0 z-50" onClick={() => setIsOpen(false)}>
          <div 
            className="absolute bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto min-w-[200px]"
            style={{
              top: dropdownRef.current?.getBoundingClientRect().bottom + window.scrollY + 4,
              left: dropdownRef.current?.getBoundingClientRect().left + window.scrollX,
              width: dropdownRef.current?.getBoundingClientRect().width
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {categories.length === 0 ? (
              <div className="px-3 py-2 text-sm text-gray-500 flex items-center">
                <AlertCircle className="w-4 h-4 mr-2" />
                No categories available
              </div>
            ) : (
              <div role="listbox" className="py-1">
                {categories.map((category) => (
                  <label 
                    key={category.id} 
                    className="flex items-center px-3 py-2 hover:bg-blue-50 cursor-pointer transition-colors group"
                    role="option"
                    aria-selected={selectedIds.includes(category.id)}
                  >
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(category.id)}
                      onChange={() => handleToggleCategory(category.id)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mr-3"
                      tabIndex={-1}
                    />
                    <span className="text-sm text-gray-900 flex-1">
                      {category.nameEn || category.name || "Unnamed Category"}
                    </span>
                    {selectedIds.includes(category.id) && (
                      <Check className="w-4 h-4 text-blue-600" />
                    )}
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function MealSpecificationsTab({ 
  values, 
  setField, 
  t, 
  categories, 
  addMealSpecification, 
  removeMealSpecification, 
  updateMealSpecification 
}) {
  return (
    <div className="space-y-8">
      {/* Header Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="bg-gradient-to-r from-indigo-50 to-purple-50 px-6 py-4 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <div className="p-2 bg-indigo-100 rounded-lg mr-3">
                  <Utensils className="w-5 h-5 text-indigo-600" />
                </div>
                {t("mealSpecifications")}
              </h3>
              <p className="text-sm text-gray-600 mt-1">{t('defineMealOptions')}</p>
            </div>
            <button
              type="button"
              onClick={addMealSpecification}
              className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors shadow-sm"
            >
              <Plus className="w-4 h-4 mr-2" />
              {t("addMealSpecification")}
            </button>
          </div>
        </div>

        {(values.mealSpecifications || []).length === 0 ? (
          <div className="text-center py-12 px-6">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
              <Package className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">{t('noMealSpecifications')}</h3>
            <p className="text-sm text-gray-500 mb-6 max-w-sm mx-auto">{t('noMealSpecificationsMessage')}</p>
            <button
              type="button"
              onClick={addMealSpecification}
              className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors"
            >
              <Plus className="w-4 h-4 mr-2" />
              {t('addFirstMealSpecification')}
            </button>
          </div>
        ) : (
          <div className="p-6">
            <div className="space-y-6">
              {(values.mealSpecifications || []).map((spec, index) => (
                <div key={index} className="bg-gray-50 border border-gray-200 rounded-xl overflow-hidden">
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-gray-200">
                    <div className="flex justify-between items-center">
                      <div className="flex items-center">
                        <div className="w-8 h-8 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center text-sm font-bold mr-3">
                          {index + 1}
                        </div>
                        <div>
                          <h4 className="text-lg font-semibold text-gray-800">
                            {t("mealSpecifications")} {index + 1}
                          </h4>
                          <p className="text-sm text-gray-600">{t('configureMealType')}</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeMealSpecification(index)}
                        className="inline-flex items-center px-3 py-2 bg-red-100 text-red-700 text-sm font-medium rounded-lg hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        {t("removeMealSpecification")}
                      </button>
                    </div>
                  </div>

                  <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                      {/* Meal Type Dropdown */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          <Utensils className="w-4 h-4 inline mr-1" />
                          {t("mealType")}
                          <span className="text-red-500 ml-1">*</span>
                        </label>
                        <select
                          value={spec.mealType || "BREAKFAST"}
                          onChange={(e) => updateMealSpecification(index, "mealType", e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white transition-colors"
                          required
                        >
                          {MealTypes.map((type) => (
                            <option key={type} value={type}>
                              {t(`mealTypeLabels.${type.toLowerCase()}`)}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Number of Meals with enhanced +/- buttons */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          <Package className="w-4 h-4 inline mr-1" />
                          {t("numberOfMeals")}
                          <span className="text-red-500 ml-1">*</span>
                        </label>
                        <div className="flex items-center bg-gray-50 rounded-lg border border-gray-300 overflow-hidden">
                          <button
                            type="button"
                            onClick={() => updateMealSpecification(index, "numberOfMeals", Math.max(1, (spec.numberOfMeals || 1) - 1))}
                            className="p-3 text-gray-600 hover:text-gray-800 hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-inset disabled:opacity-50 disabled:cursor-not-allowed"
                            disabled={spec.numberOfMeals <= 1}
                          >
                            <Minus className="w-4 h-4" />
                          </button>
                          <div className="px-4 py-3 text-center font-semibold text-gray-900 bg-white min-w-[4rem] border-x border-gray-300">
                            {spec.numberOfMeals || 1}
                          </div>
                          <button
                            type="button"
                            onClick={() => updateMealSpecification(index, "numberOfMeals", (spec.numberOfMeals || 1) + 1)}
                            className="p-3 text-gray-600 hover:text-gray-800 hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-inset"
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                        </div>
                      </div>

                      {/* Summary Card */}
                      <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                        <div className="text-center">
                          <div className="text-sm text-gray-600 mb-1">{t('summary')}</div>
                          <div className="text-lg font-semibold text-gray-900">
                            {spec.numberOfMeals || 1}x {t(`mealTypes.${(spec.mealType || 'BREAKFAST').toLowerCase()}`)}
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            {(spec.allowedCategoryIds || []).length} {t('categoryCount')}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Categories Selection */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <Package className="w-4 h-4 inline mr-1" />
                        {t("allowedCategories")}
                        <span className="text-red-500 ml-1">*</span>
                      </label>
                      <CategoriesDropdown
                        categories={categories}
                        selectedIds={spec.allowedCategoryIds || []}
                        onChange={(categoryIds) => updateMealSpecification(index, "allowedCategoryIds", categoryIds)}
                        placeholder={t("selectCategories")}
                      />
                      <div className="text-xs text-gray-500 mt-1 flex items-center">
                        <AlertCircle className="w-3 h-3 mr-1" />
                        {t('categoriesHelp')}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
